import { useMemo } from 'react';

import type { PayV3Req } from '@revelio/data-access';
import { NoData } from '@revelio/replots';

import { useSelectionListsForKeys } from '../../../../../shared/selection-lists/hooks/useSelectionListsForKeys';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { getPayV3Variables } from '../../../filters/utils/pay-v3/get-pay-v3-variables';
import { usePlotMetric } from '../../../hooks/usePlotMetric';
import { useChart } from '../../Chart/ChartProvider';
import type { MapPoint } from '../../Chart/Map/types';
import { determineLocationType } from '../../Chart/Map/utils/location-type-utils';
import {
  isPayMetric,
  type PayMetricValues,
} from '../../PageConfigProvider/types/metric';
import type { DataProviderChildren } from '../types';
import {
  extractGranularitiesWithIds,
  useCoordinateResolverForMixedGranularities,
  withCoordinateStatus,
} from '../utils/coordinate-resolver';
import { combineQueryStatus } from '../utils/data-provider-results-utils';
import { usePayData } from './use-pay-data';

type CompensationMapDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  exchangeRate?: number;
  children: DataProviderChildren<{ points: MapPoint[] } | undefined>;
};

// There are currently a bit more than 1000 geography entities.
// Going forward, we could either increase of make multiple queries to get data for each zoom level.
const MAX_TOP_GEOS_ENTITIES = 1024;

/**
 * The geo enrichment only rewrites `filters.region/country/msa`, so it is
 * computed once as a patch. `null` means "send the base filters untouched".
 */
type GeoPatch = {
  region: number[];
  country: number[];
  msa: number[];
} | null;

const applyGeoPatch = (req: PayV3Req, patch: GeoPatch): PayV3Req => ({
  ...req,
  ...(patch ? { filters: { ...req.filters, ...patch } } : {}),
  options: { required_top_geos_length: MAX_TOP_GEOS_ENTITIES },
});

export const CompensationMapDataProvider = ({
  primaryDimension,
  exchangeRate,
  children,
}: CompensationMapDataProviderProps) => {
  const { item: _item } = useChart();
  const metric = usePlotMetric();
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { selectionLists } = useSelectionListsForKeys(['region', 'country']);
  const { region: regionList, country: countryList } = selectionLists;
  const compensationVariables = getPayV3Variables({
    primaryDimension,
    effectiveFilters,
  });

  // Create filter patch configurations for dual requests when regions are selected
  const { regionPatch, countryPatch, needsDualRequest } = useMemo((): {
    regionPatch: GeoPatch;
    countryPatch: GeoPatch;
    needsDualRequest: boolean;
  } => {
    // Wait until both universe lists have loaded before enriching — they
    // arrive together from one geo-tree fetch, and enriching with a half-loaded
    // universe would send the country query with an empty id list.
    if (!regionList || !countryList) {
      return { regionPatch: null, countryPatch: null, needsDualRequest: false };
    }

    const { region, country, msa } = compensationVariables.filters;

    // If no geo filters are selected, use all regions
    if (!region?.length && !country?.length && !msa?.length) {
      const enrichedRegion = regionList.map((r) => Number(r.id));
      return {
        regionPatch: { region: enrichedRegion, country: [], msa: [] },
        countryPatch: null,
        needsDualRequest: false,
      };
    }
    // If only regions are selected, we need dual requests
    else if (region?.length && !country?.length && !msa?.length) {
      const enrichedCountry = (countryList ?? [])
        .filter((c) => region.includes(Number(c.region)))
        .map((c) => Number(c.id));

      return {
        regionPatch: { region: region, country: [], msa: [] },
        countryPatch: { region: [], country: enrichedCountry, msa: [] },
        needsDualRequest: true,
      };
    }
    // For other cases (countries or MSAs selected), use single request
    else {
      return { regionPatch: null, countryPatch: null, needsDualRequest: false };
    }
  }, [compensationVariables, regionList, countryList]);

  // First request (always made)
  const { data: regionData, status: regionStatus } = usePayData({
    variant: 'topGeos',
    req: applyGeoPatch(compensationVariables, regionPatch),
  });

  // Second request (only when dual request is needed)
  const { data: countryData, status: countryStatus } = usePayData({
    variant: 'topGeos',
    req: applyGeoPatch(compensationVariables, countryPatch),
    pause: !needsDualRequest || !countryPatch,
  });

  // Combine status and data from both requests
  const status = needsDualRequest
    ? combineQueryStatus([regionStatus, countryStatus])
    : regionStatus;

  const data = useMemo(() => {
    if (!needsDualRequest) {
      return regionData;
    }

    // Merge data from both requests
    if (!regionData?.pay_v3?.entities || !countryData?.pay_v3?.entities) {
      return regionData || countryData;
    }

    // Combine entities from both requests
    const combinedEntities = [
      ...regionData.pay_v3.entities,
      ...countryData.pay_v3.entities,
    ];

    return {
      ...regionData,
      pay_v3: {
        ...regionData.pay_v3,
        entities: combinedEntities,
      },
    };
  }, [regionData, countryData, needsDualRequest]);

  const geoData = useMemo(() => {
    if (!data?.pay_v3?.entities) return [];

    const metricField = (() => {
      if (metric === 'base_salary') return 'base';
      if (metric === 'total_compensation') return 'total';
      return 'hourly';
    })();

    // Group by geography ID and collect all values
    const geoMap = new Map<
      number,
      {
        id: number;
        label: string;
        values: number[];
        entityValues: Record<string, number>;
      }
    >();

    data.pay_v3.entities.forEach((entity) => {
      entity?.metrics?.top_geos?.forEach((item) => {
        const geoId = item.id ?? 0;
        const rawValue = item[metricField]?.[1] ?? 0;
        const value = exchangeRate ? rawValue * exchangeRate : rawValue;

        if (geoId > 0 && value > 0) {
          const entityName =
            entity.short_name || entity.long_name || 'Unknown Entity';

          if (!geoMap.has(geoId)) {
            geoMap.set(geoId, {
              id: geoId,
              label: item.short_name ?? '',
              values: [],
              entityValues: {},
            });
          }

          const geo = geoMap.get(geoId);
          if (!geo) return;

          geo.values.push(value);
          geo.entityValues[entityName] = value;
        }
      });
    });

    // Calculate averages
    return Array.from(geoMap.values()).map((geo) => ({
      id: geo.id,
      label: geo.label,
      value: geo.values.reduce((sum, val) => sum + val, 0) / geo.values.length,
      tooltip: geo.entityValues,
    }));
  }, [data, metric, exchangeRate]);

  const granularitiesWithIds = extractGranularitiesWithIds(geoData);

  const { data: coords, ...coordsState } =
    useCoordinateResolverForMixedGranularities(granularitiesWithIds);

  const points = useMemo((): MapPoint[] => {
    if (!coords.length || !geoData.length) return [];
    const coordMap = new Map(coords.map((c) => [c.id, c] as const));
    return geoData
      .map((d) => {
        const c = coordMap.get(d.id);
        if (!c) return null;
        const label = d.label || c.name;
        const locationType = determineLocationType(label, c.granularity);
        return {
          id: String(d.id),
          label,
          lat: c.lat,
          lon: c.lon,
          value: d.value,
          circleRadius: 0,
          locationType,
          ...(Object.keys(d.tooltip).length > 0 && { tooltip: d.tooltip }),
        };
      })
      .filter(
        (point): point is NonNullable<typeof point> => point !== null
      ) as MapPoint[];
  }, [coords, geoData]);

  if (!isPayMetric(metric as PayMetricValues)) {
    return <NoData />;
  }

  return children({
    data: { points },
    ...withCoordinateStatus(status, coordsState),
  });
};
